import React from 'react';

import { Box, Chip, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type {
  ActionLibraryInstanceListParams,
  InstanceDashboardMetricsCreatedPeriod,
} from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';

import useStyles from './InstanceStatistics.styles';

type Props = {
  metricsParams: ActionLibraryInstanceListParams | undefined;
};

const InstanceStatistics: React.FC<Props> = ({ metricsParams }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const {
    data: metrics,
    isLoading,
    isError,
  } = useQuery(
    ['ACTION_LIBRARY_INSTANCE_METRICS', metricsParams],
    async () => {
      const { data } = await actionLibraryInstancesApi.getActionLibraryInstanceMetrics(metricsParams);
      return data;
    },
    {
      enabled: metricsParams !== undefined,
      keepPreviousData: true,
    },
  );

  if (isLoading || isError || !metrics) {
    return null;
  }

  const completedPercent = Math.round(metrics.percentageCompletedInstances);
  const totalInstances =
    metrics.percentageCompletedInstances >= 100
      ? metrics.activeInstancesCount
      : Math.round(metrics.activeInstancesCount / (1 - metrics.percentageCompletedInstances / 100));

  const periodDetail = (period: InstanceDashboardMetricsCreatedPeriod) =>
    `${period.completed} ${t(action_library_messages.table.instance_status.completed)}, ${period.not_started} ${t(
      action_library_messages.table.instance_status.not_started,
    )}`;

  return (
    <Box className={styles.root}>
      <Box className={styles.statsRow}>
        <Box className={styles.statBox}>
          <Box className={styles.statItem}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.statistics.total)}
            </Typography>
            <Chip className={`${styles.chip} ${styles.chipDefault}`} label={totalInstances} size='small' variant='outlined' />
          </Box>
        </Box>
        <Box className={styles.statBox}>
          <Box className={styles.statItem}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.statistics.active)}
            </Typography>
            <Chip className={`${styles.chip} ${styles.chipInfo}`} label={metrics.activeInstancesCount} size='small' variant='outlined' />
          </Box>
        </Box>
        <Box className={styles.statBox}>
          <Box className={styles.statItem}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.statistics.overdue)}
            </Typography>
            <Chip
              className={`${styles.chip} ${metrics.overdueActiveInstancesCount > 0 ? styles.chipSecondary : styles.chipDefault}`}
              label={metrics.overdueActiveInstancesCount}
              size='small'
              variant='outlined'
            />
          </Box>
        </Box>
        <Box className={styles.statBox}>
          <Box className={styles.statItem}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.statistics.completed_percent)}
            </Typography>
            <Chip className={`${styles.chip} ${styles.chipSuccess}`} label={`${completedPercent}%`} size='small' variant='outlined' />
          </Box>
        </Box>
        <Box className={styles.statBox}>
          <Box className={styles.periodStatInner}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.statistics.created_last_30_days)}
            </Typography>
            <Chip
              className={`${styles.chip} ${styles.chipDefault}`}
              label={metrics.createdLast30Days.total}
              size='small'
              variant='outlined'
            />
          </Box>
          <Typography className={styles.periodDetail} variant='caption'>
            {periodDetail(metrics.createdLast30Days)}
          </Typography>
        </Box>
        <Box className={styles.statBox}>
          <Box className={styles.periodStatInner}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.statistics.created_last_90_days)}
            </Typography>
            <Chip className={`${styles.chip} ${styles.chipInfo}`} label={metrics.createdLast90Days.total} size='small' variant='outlined' />
          </Box>
          <Typography className={styles.periodDetail} variant='caption'>
            {periodDetail(metrics.createdLast90Days)}
          </Typography>
        </Box>
        <Box className={styles.statBox}>
          <Box className={styles.periodStatInner}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.statistics.created_last_365_days)}
            </Typography>
            <Chip
              className={`${styles.chip} ${styles.chipWarning}`}
              label={metrics.createdLast365Days.total}
              size='small'
              variant='outlined'
            />
          </Box>
          <Typography className={styles.periodDetail} variant='caption'>
            {periodDetail(metrics.createdLast365Days)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default InstanceStatistics;
